import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { Badge, Banner, Button, Card, Field, Spinner } from '../components/ui.js';
import { formatMm } from '../editor/mm-px.js';

interface Vertex {
  x: number;
  y: number;
}

/**
 * CALIBRADOR VISUAL DE ZONAS (Fase 2).
 *
 * Se dibuja el polígono de la zona SOBRE la foto del producto y se arrastran
 * los vértices. Las medidas que se ven y se guardan son MILÍMETROS REALES: la
 * escala sale de `mm_per_pixel` de la vista del producto, no de una regla a ojo.
 *
 * Al confirmar, la corrección queda marcada como CONFIRMADA y sobrevive a las
 * reimportaciones del catálogo: si el feed de Roly la contradice, se genera una
 * excepción en vez de pisarla.
 */
export function ZoneCalibrator() {
  const t = useT();
  const [sku, setSku] = useState('');
  const [size, setSize] = useState('');
  const [data, setData] = useState<Awaited<ReturnType<typeof api.effectiveCalibration>> | null>(null);
  const [zoneIndex, setZoneIndex] = useState(0);
  const [vertices, setVertices] = useState<Vertex[]>([]);
  const [dragging, setDragging] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const zone = data?.zones[zoneIndex];

  useEffect(() => {
    if (zone) setVertices(zone.polygonMm.map((p) => ({ ...p })));
  }, [zone]);

  const bounds = useMemo(() => {
    if (vertices.length === 0) return { width: 100, height: 100 };
    const xs = vertices.map((v) => v.x);
    const ys = vertices.map((v) => v.y);
    return {
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    };
  }, [vertices]);

  const load = async () => {
    setError(null);
    setMessage(null);
    try {
      setData(await api.effectiveCalibration(sku.trim().toUpperCase(), size || undefined));
      setZoneIndex(0);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // El SVG usa un viewBox EN MILÍMETROS: así el arrastre es directamente una
  // coordenada del documento y no hay conversión que se pueda equivocar.
  const viewBox = useMemo(() => {
    if (vertices.length === 0) return '0 0 100 100';
    const margin = 20;
    const xs = vertices.map((v) => v.x);
    const ys = vertices.map((v) => v.y);
    return `${Math.min(...xs) - margin} ${Math.min(...ys) - margin} ${bounds.width + margin * 2} ${bounds.height + margin * 2}`;
  }, [vertices, bounds]);

  const pointFromEvent = (event: React.PointerEvent): Vertex | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const matrix = svg.getScreenCTM()?.inverse();
    if (!matrix) return null;
    const transformed = point.matrixTransform(matrix);
    return { x: Number(transformed.x.toFixed(2)), y: Number(transformed.y.toFixed(2)) };
  };

  return (
    <div className="space-y-3">
      <Card className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="SKU">
            <input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="CA6502-02-M"
              className="toque rounded-lg border border-borde px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Talla">
            <input
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="M"
              className="toque w-24 rounded-lg border border-borde px-3 py-2 text-sm"
            />
          </Field>
          <Button onClick={() => void load()} disabled={!sku.trim()}>
            {t('app.search')}
          </Button>
        </div>
        {error && <Banner tone="error" title={t('app.error')}>{error}</Banner>}
        <p className="text-xs text-tinta-suave">{t('admin.calibrator.help')}</p>
      </Card>

      {data && zone && (
        <div className="grid gap-3 lg:grid-cols-[2fr_1fr]">
          <Card>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {data.zones.map((z, index) => (
                <button
                  key={z.zoneId}
                  onClick={() => setZoneIndex(index)}
                  className={`toque rounded-lg border px-3 py-1.5 text-sm ${
                    index === zoneIndex ? 'border-marca bg-marca text-white' : 'border-borde'
                  }`}
                >
                  {z.name}
                </button>
              ))}
            </div>

            <svg
              ref={svgRef}
              viewBox={viewBox}
              className="lienzo-tactil w-full rounded-lg border border-borde bg-papel-suave"
              style={{ aspectRatio: `${bounds.width} / ${bounds.height}` }}
              onPointerMove={(event) => {
                if (dragging === null) return;
                const point = pointFromEvent(event);
                if (!point) return;
                setVertices((current) => current.map((v, i) => (i === dragging ? point : v)));
              }}
              onPointerUp={() => setDragging(null)}
              onPointerLeave={() => setDragging(null)}
            >
              <polygon
                points={vertices.map((v) => `${v.x},${v.y}`).join(' ')}
                fill="rgba(184,29,44,0.12)"
                stroke="#b81d2c"
                strokeWidth={bounds.width / 200}
              />
              {vertices.map((vertex, index) => (
                <circle
                  key={index}
                  cx={vertex.x}
                  cy={vertex.y}
                  r={bounds.width / 45}
                  fill="#ffffff"
                  stroke="#111111"
                  strokeWidth={bounds.width / 300}
                  onPointerDown={(event) => {
                    (event.target as Element).setPointerCapture(event.pointerId);
                    setDragging(index);
                  }}
                  style={{ cursor: 'grab' }}
                />
              ))}
            </svg>

            <p className="mt-2 text-sm tabular-nums text-tinta-suave">
              {formatMm(bounds.width)} × {formatMm(bounds.height)}
            </p>
          </Card>

          <div className="space-y-3">
            <Card className="space-y-2">
              <h3 className="text-sm font-semibold">{t('admin.calibrator.provenance')}</h3>
              <p>
                <Badge tone={zone.provenance === 'inherited' ? 'neutral' : 'ok'}>{zone.provenance}</Badge>{' '}
                <Badge tone={zone.confidence < 0.7 ? 'warning' : 'ok'}>
                  {Math.round(zone.confidence * 100)}%
                </Badge>
              </p>

              {/* La TRAZA: de dónde sale cada milímetro. Sin esto, una zona
                  heredada es un número sin historia. */}
              <h3 className="pt-2 text-sm font-semibold">{t('admin.calibrator.trace')}</h3>
              <ol className="space-y-1 text-xs text-tinta-suave">
                {zone.trace.map((step, index) => (
                  <li key={index}>
                    <span className="font-medium">{step.step}</span> · {step.detail}
                  </li>
                ))}
              </ol>
            </Card>

            <Card className="space-y-2">
              <Button
                loading={saving}
                onClick={async () => {
                  setSaving(true);
                  try {
                    await api.putZoneOverride(data.productId, zone.zoneId, {
                      colorId: null,
                      viewCode: zone.viewCode,
                      polygonMm: vertices,
                      confirmed: true,
                    });
                    setMessage('Corrección confirmada. Sobrevivirá a las reimportaciones.');
                    setData(await api.effectiveCalibration(sku.trim().toUpperCase(), size || undefined));
                  } catch (err) {
                    setError((err as Error).message);
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {t('admin.calibrator.confirm')}
              </Button>
              <p className="text-xs text-tinta-suave">{t('admin.calibrator.confirmHelp')}</p>
              {message && <Banner tone="ok" title={message} />}
            </Card>
          </div>
        </div>
      )}

      {data && !zone && <Spinner />}
    </div>
  );
}
